/**
 * Soft-deferred playlist swaps wait for first-frame (head) readiness before rematch,
 * unless the starvation ceiling forces a best-effort apply.
 */

export type TPendingPlaylistSwapPhase = 'downloading' | 'ready_at_boundary' | 'rematching'

/**
 * Why soft-defer entered rematch/apply — field soaks distinguish head-wait vs ready-cap vs
 * starvation force without reading overlay copy.
 */
export type TPendingPlaylistSwapFlushReason
  = 'boundary' | 'ready_timer' | 'force_deadline' | 'heal'

/**
 * Whether a frame/loop boundary (or timer) may apply the deferred registration root.
 * `phase === null` means no swap-progress tracking (treat as ready).
 * `rematching` means apply already in flight — do not re-enter.
 */
export function shouldApplyDeferredRegistrationPlaybackFlush(options: {
  phase: TPendingPlaylistSwapPhase | null
  force: boolean
}): boolean {
  if (options.phase === 'rematching') {
    return false
  }
  if (options.force) {
    return true
  }
  if (options.phase == null) {
    return true
  }
  return options.phase === 'ready_at_boundary'
}

/**
 * Classify the cutover trigger for diagnostics heartbeats.
 * Call before flipping phase to `rematching`.
 */
export function resolvePendingPlaylistSwapFlushReason(options: {
  force: boolean
  phase: TPendingPlaylistSwapPhase | null
}): TPendingPlaylistSwapFlushReason {
  if (options.phase === 'ready_at_boundary' || options.phase === 'rematching') {
    return options.force ? 'ready_timer' : 'boundary'
  }
  return 'force_deadline'
}

/**
 * Progress refresh must not regress `ready_at_boundary` → `downloading` for the **same** head
 * set (stale overlapping IDB scans). If the resolved head key changes, re-gate on progress.
 * Never regress `rematching` (apply in flight).
 */
export function resolvePendingPlaylistSwapPhaseUpdate(options: {
  previousPhase: TPendingPlaylistSwapPhase | null
  progress01: number
  headKey: string
  previousHeadKey: string | null
}): TPendingPlaylistSwapPhase {
  if (options.previousPhase === 'rematching') {
    return 'rematching'
  }
  if (options.progress01 >= 1) {
    return 'ready_at_boundary'
  }
  if (
    options.previousPhase === 'ready_at_boundary'
    && options.previousHeadKey != null
    && options.previousHeadKey === options.headKey
  ) {
    return 'ready_at_boundary'
  }
  return 'downloading'
}

/**
 * Repeated CAMPAIGN_UPDATE for the same pending identity must not restart prefetch.
 * Restart bumps gen, resets progress to 0%, and can drop `ready_at_boundary` — field
 * lag of 2–3 outgoing videos while Studio assign / polls keep the same Campanha root.
 * Callers must also keep the existing `deferredRegistrationPlaybackRoot` object when
 * this returns false — replacing it aborts in-flight prefetch (`!== root` checks).
 */
export function shouldRestartPendingPlaylistPrefetch(options: {
  samePending: boolean
  hasSwapState: boolean
}): boolean {
  return !options.samePending || !options.hasSwapState
}
